import * as React from "react";
import {memo} from "react";
import AsyncSelect from "react-select/async";
import {withTheme} from "@emotion/react";
import {FormErrorMessage} from "@web2/form-fields";
import {IFormFieldProps} from "@web2/form2";

import {Label, SelectSize, SelectWrapper} from "./Select";
import {ISelectTheme, selectCustomStyles} from "./Styles";

type ISelectWithTheme = {theme: ISelectTheme};

interface IProps extends IFormFieldProps<string, IAsyncSelectOption[]>, ISelectWithTheme {
    // any: AsyncSelect expects a non-mutable value, we dont
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any;
    clearable?: boolean;
    groupClassName?: string;
    className?: string;
    label?: string;
    required?: boolean;
    loadOptions: (input: string) => void;
    fetchData?: (values: (string | number | boolean)[]) => Promise<IAsyncSelectOption[]>;
    placeholder?: string;
    searchable?: boolean;
    id?: string;
    errorOnBottom?: boolean;
    isMulti?: boolean;
    errorClassName?: string;
    size?: SelectSize;
}

interface SelectAsyncState {
    isLoading: boolean;
}

export interface IAsyncSelectOption<TValue = string | number | boolean> {
    label?: string;
    value: TValue;
}

export class SelectAsyncC extends React.PureComponent<IProps, SelectAsyncState> {
    public static defaultProps = {
        clearable: false,
        searchable: false,
        ignoreAccents: false
    };

    constructor(props: IProps) {
        super(props);
        this.state = {isLoading: false};
    }

    /**
     * Callback
     */

    private onChange = (value: readonly IAsyncSelectOption[]): void => {
        // casting: AsyncSelect expects a non-mutable value, we don't
        this.props.onChange(this.props.name, value as IAsyncSelectOption[]);
    };

    private customStyles = selectCustomStyles(this.props.theme, true);

    /**
     * Render
     */

    public render() {
        return (
            <SelectWrapper error={this.props.error} errorOnBottom={this.props.errorOnBottom} data-testid="multiselect-wrapper">
                {this.props.label && (
                    <Label htmlFor={this.props.id} data-testid={`multiselect-label-${this.props.name}`}>
                        {this.props.label} {this.props.required && <span data-testid={`multiselect-required-asterisk-${this.props.name}`}>*</span>}
                    </Label>
                )}

                {this.props.error && !this.props.errorOnBottom && <FormErrorMessage error={this.props.error} />}

                <AsyncSelect
                    name={this.props.name}
                    id={this.props.id}
                    value={this.props.value}
                    onChange={this.onChange}
                    clearable={this.props.clearable || false}
                    searchable={this.props.searchable || false}
                    cache={false}
                    loadOptions={this.props.loadOptions}
                    isLoading={this.state.isLoading}
                    placeholder={this.props.placeholder || "Wybierz"}
                    searchPromptText="Zacznij pisać żeby zobaczyć wyniki"
                    loadingMessage={() => "Ładowanie..."}
                    noOptionsMessage={() => "Nie znaleziono wyników"}
                    openOnFocus={true}
                    tabSelectsValue={false}
                    instanceId={this.props.name}
                    defaultOptions
                    isMulti={this.props.isMulti}
                    error={this.props.error}
                    styles={this.customStyles}
                    size={this.props.size}
                />

                {this.props.error && this.props.errorOnBottom && <FormErrorMessage error={this.props.error} className={this.props.errorClassName} />}
            </SelectWrapper>
        );
        // `instanceId` required - https://github.com/JedWatson/react-select/pull/1105
    }
}

export const SelectAsync = memo(withTheme(SelectAsyncC));
